import { ResponsiveBar } from "@nivo/bar";
import { ColorSchemeId } from "@nivo/colors";
import { ResponsiveLine } from "@nivo/line";
import { ResponsivePie } from "@nivo/pie";
import dayjs from "dayjs";
import { Fragment } from "react/jsx-runtime";
import {
  BarChart as RechartsBarChart,
  PieChart as RechartsPieChart,
  Area,
  Bar,
  Brush,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Cell,
  Pie,
  LabelList,
  Text,
} from "recharts";
import { CHART_COLORS } from "utils/constants";
import { formatCurrencyShort, formatMoney, formatPercent, toTitleCase } from "utils/functions";

type T_LineChartData = { x: number | string; y: number | string }[];
// make sure parent container have a defined height when using
// responsive component, otherwise height will be 0 and
// no chart will be rendered.
// website examples showcase many properties,
// you'll often use just a few of them.
export const LineChart = ({ data, legend }: { data: T_LineChartData; legend: { x: string; y?: string } }) => (
  <ResponsiveLine
    data={[
      {
        id: "1",
        data: data,
        color: "#a855f7",
      },
    ]}
    margin={{ top: 10, right: 10, bottom: 50, left: 50 }}
    xScale={{ type: "point" }}
    yScale={{
      type: "linear",
      min: "auto",
      max: "auto",
      stacked: false,
      reverse: false,
    }}
    yFormat={(data) => formatMoney(data as unknown as number)}
    axisTop={null}
    axisRight={null}
    axisBottom={{
      tickSize: 5,
      tickPadding: 5,
      tickRotation: 0,
      legend: legend.x,
      legendOffset: 36,
      legendPosition: "middle",
      truncateTickAt: 0,
    }}
    axisLeft={{
      tickSize: 5,
      tickPadding: 5,
      tickRotation: 0,
      legend: legend?.y ?? "",
      legendOffset: -40,
      legendPosition: "middle",
      truncateTickAt: 0,
      format: (val) => {
        if (val > 1000) return `${formatMoney(Math.round(val / 1000))}k`;
        else return val;
      },
    }}
    colors={{ datum: "color" }}
    pointSize={8}
    // pointColor={{ theme: "background" }}
    pointBorderWidth={2}
    pointBorderColor={{ from: "background" }}
    pointLabel="data.yFormatted"
    pointLabelYOffset={-12}
    enableTouchCrosshair={true}
    useMesh={true}
    // legends={[
    //   {
    //     anchor: "bottom-right",
    //     direction: "column",
    //     justify: false,
    //     translateX: 100,
    //     translateY: 0,
    //     itemsSpacing: 0,
    //     itemDirection: "left-to-right",
    //     itemWidth: 80,
    //     itemHeight: 20,
    //     itemOpacity: 0.75,
    //     symbolSize: 12,
    //     symbolShape: "circle",
    //     symbolBorderColor: "rgba(0, 0, 0, .5)",
    //     effects: [
    //       {
    //         on: "hover",
    //         style: {
    //           itemBackground: "rgba(0, 0, 0, .03)",
    //           itemOpacity: 1,
    //         },
    //       },
    //     ],
    //   },
    // ]}
  />
);

export function BarChart<
  T_Data extends { [key: string]: string | number },
  T_Key extends keyof T_Data,
  T_IndexKey extends T_Key,
  T_OtherKeys extends Exclude<keyof T_Data, T_IndexKey>[],
>({
  data,
  keys,
  legend,
  indexBy,
  groupMode,
  colorTheme,
}: {
  data: T_Data[];
  indexBy: T_IndexKey extends string ? T_IndexKey : never;
  keys: T_OtherKeys extends string[] ? T_OtherKeys : never;
  legend?: { x: string; y?: string };
  groupMode: "grouped" | "stacked";
  colorTheme?: ColorSchemeId;
}) {
  return (
    <ResponsiveBar
      data={data}
      margin={{ top: 10, right: 150, bottom: 50, left: 50 }}
      // xScale={{ type: "point" }}
      // yScale={{
      //   type: "linear",
      //   min: "auto",
      //   max: "auto",
      //   stacked: false,
      //   reverse: false,
      // }}
      valueFormat={(data) => formatMoney(data as unknown as number)}
      groupMode={groupMode}
      keys={keys}
      indexBy={indexBy}
      axisTop={null}
      axisRight={null}
      axisBottom={{
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,
        legend: legend?.x ?? "",
        legendOffset: 36,
        legendPosition: "middle",
        truncateTickAt: 0,
      }}
      axisLeft={{
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,
        legend: legend?.y ?? "",
        legendOffset: -40,
        legendPosition: "middle",
        truncateTickAt: 0,
        format: (val) => {
          if (val > 1000) return `${formatMoney(Math.round(val / 1000))}k`;
          else return val;
        },
      }}
      enableLabel={false}
      colors={{ scheme: colorTheme ?? "paired" }}
      // pointSize={8}
      // pointColor={{ theme: "background" }}
      // pointBorderWidth={2}
      // pointBorderColor={{ from: "background" }}
      // pointLabel="data.yFormatted"
      // pointLabelYOffset={-12}
      // enableTouchCrosshair={true}
      // useMesh={true}
      legends={[
        {
          dataFrom: "keys",
          anchor: "bottom-right",
          direction: "column",
          justify: false,
          translateX: 100,
          translateY: 0,
          itemsSpacing: 0,
          itemDirection: "left-to-right",
          itemWidth: 80,
          itemHeight: 20,
          itemOpacity: 0.75,
          symbolSize: 12,
          symbolShape: "circle",
          symbolBorderColor: "rgba(0, 0, 0, .5)",
          effects: [
            {
              on: "hover",
              style: {
                itemBackground: "rgba(0, 0, 0, .03)",
                itemOpacity: 1,
              },
            },
          ],
        },
      ]}
    />
  );
}

export const LineChartMultiple = ({
  data,
  legend,
}: {
  data: { id: string; data: T_LineChartData; color?: string }[];
  legend: { x: string; y?: string };
}) => {
  return (
    <ResponsiveLine
      data={data}
      margin={{ top: 10, right: 100, bottom: 50, left: 50 }}
      yScale={{
        type: "linear",
        min: "auto",
        max: "auto",
        stacked: false,
        reverse: false,
      }}
      xFormat="time:%B %Y"
      xScale={{
        format: "%B %Y",
        precision: "month",
        type: "time",
      }}
      yFormat={(data) => formatMoney(data as unknown as number)}
      axisTop={null}
      axisRight={null}
      axisBottom={{
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,
        legend: legend.x,
        legendOffset: 36,
        legendPosition: "middle",
        truncateTickAt: 0,
        format: "%b %Y",
        tickValues: "every 1 month",
      }}
      axisLeft={{
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,
        legend: legend?.y ?? "",
        legendOffset: -40,
        legendPosition: "middle",
        truncateTickAt: 0,
        format: (value) => {
          const val = Number(value);
          if (val > 1000) return `${formatMoney(Math.round(val / 1000))}k`;
          else if (val < -1000) return `${formatMoney(Math.round(val / 1000))}k`;
          else return val;
        },
      }}
      curve="catmullRom"
      // colors={{ datum: "color" }}
      colors={{ scheme: "set1" }}
      pointSize={8}
      // pointColor={{ theme: "background" }}
      pointBorderWidth={2}
      pointBorderColor={{ from: "background" }}
      pointLabel="data.yFormatted"
      pointLabelYOffset={-12}
      enableTouchCrosshair={true}
      useMesh={true}
      legends={[
        {
          anchor: "bottom-right",
          direction: "column",
          justify: false,
          translateX: 100,
          translateY: 0,
          itemsSpacing: 0,
          itemDirection: "left-to-right",
          itemWidth: 80,
          itemHeight: 20,
          // itemOpacity: 0.75,
          itemOpacity: 1,
          symbolSize: 12,
          symbolShape: "circle",
          symbolBorderColor: "rgba(0, 0, 0, .5)",
          effects: [
            {
              on: "hover",
              style: {
                itemBackground: "rgba(0, 0, 0, .03)",
                itemOpacity: 1,
              },
            },
          ],
        },
      ]}
    />
  );
};

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="p-2 bg-white shadow rounded border">
        <p className="text-gray-700">{payload[0].payload.x}</p>
        <p className="text-green-600 font-bold">{formatMoney(payload[0].value)}</p>
      </div>
    );
  }
  return null;
};

export const LineChartFilled = ({ data, legend }: { data: T_LineChartData; legend: { x: string; y?: string } }) => {
  return (
    <ResponsiveContainer>
      <ComposedChart data={data} margin={{ top: 5, right: 20, bottom: 5, left: 50 }}>
        <CartesianGrid stroke="#ccc" strokeDasharray="5 5" />
        <XAxis dataKey="x" />
        <YAxis tickFormatter={(value) => formatMoney(value)} />
        <Tooltip content={<CustomTooltip />} />
        <Area type="monotone" dataKey="y" stroke="#9333ea" fill="#d8b4fe" strokeWidth={2} />
        <Line type="monotone" dataKey="y" stroke="#9333ea" strokeWidth={2} />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

type T_PieChartData = { label: number | string; value: number; color?: string; id: string }[];
// make sure parent container have a defined height when using
// responsive component, otherwise height will be 0 and
// no chart will be rendered.
// website examples showcase many properties,
// you'll often use just a few of them.
export const PieChart = ({ data }: { data: T_PieChartData }) => (
  <ResponsivePie
    data={data}
    margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
    innerRadius={0.5}
    padAngle={0.7}
    cornerRadius={3}
    activeOuterRadiusOffset={8}
    colors={{ datum: "data.color" }}
    // borderWidth={1}
    borderColor={{
      from: "color",
      modifiers: [["darker", 0.2]],
    }}
    tooltip={(record) => {
      return (
        <div className="bg-white shadow-sm shadow-black/10 p-2 border border-solid border-gray-200 rounded-md text-sm">
          {record?.datum?.label}: <b>{formatMoney(record.datum.value)}</b>
        </div>
      );
    }}
    enableArcLabels={false}
    enableArcLinkLabels={false}
    // arcLinkLabelsSkipAngle={10}
    // arcLinkLabelsTextColor="#333333"
    // arcLinkLabelsThickness={2}
    // arcLinkLabelsColor={{ from: "color" }}
    arcLabelsSkipAngle={10}
    // arcLabelsTextColor={{
    //   from: "color",
    //   modifiers: [["darker", 4]],
    // }}
    // defs={[
    //     {
    //         id: 'dots',
    //         type: 'patternDots',
    //         background: 'inherit',
    //         color: 'rgba(255, 255, 255, 0.3)',
    //         size: 4,
    //         padding: 1,
    //         stagger: true
    //     },
    //     {
    //         id: 'lines',
    //         type: 'patternLines',
    //         background: 'inherit',
    //         color: 'rgba(255, 255, 255, 0.3)',
    //         rotation: -45,
    //         lineWidth: 6,
    //         spacing: 10
    //     }
    // ]}
    // fill={[
    //   {
    //     match: {
    //       id: "ruby",
    //     },
    //     id: "dots",
    //   },
    //   {
    //     match: {
    //       id: "c",
    //     },
    //     id: "dots",
    //   },
    //   {
    //     match: {
    //       id: "go",
    //     },
    //     id: "dots",
    //   },
    //   {
    //     match: {
    //       id: "python",
    //     },
    //     id: "dots",
    //   },
    //   {
    //     match: {
    //       id: "scala",
    //     },
    //     id: "lines",
    //   },
    //   {
    //     match: {
    //       id: "lisp",
    //     },
    //     id: "lines",
    //   },
    //   {
    //     match: {
    //       id: "elixir",
    //     },
    //     id: "lines",
    //   },
    //   {
    //     match: {
    //       id: "javascript",
    //     },
    //     id: "lines",
    //   },
    // ]}
    // legends={[
    //   {
    //     anchor: "right",
    //     direction: "column",
    //     justify: false,
    //     translateX: 0,
    //     translateY: 0,
    //     itemWidth: 100,
    //     itemHeight: 40,
    //     itemsSpacing: 0,
    //     symbolSize: 32,
    //     itemDirection: "left-to-right",
    //   },
    // ]}
  />
);

export function ReOriginalOptimalBarChart({
  data,
}: {
  data: { id: string; original_amount: number; optimal_amount: number }[];
}) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <RechartsBarChart
        width={500}
        height={300}
        data={data}
        margin={{
          top: 5,
          right: 30,
          left: 20,
          bottom: 5,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="id" />
        <YAxis tickFormatter={(value) => formatMoney(value)} />
        <Tooltip
          content={({ active, payload }) => {
            if (active && payload && payload.length) {
              return (
                <div className="p-2 bg-white shadow rounded border">
                  <p className="text-gray-700">{payload[0].payload.id}</p>
                  <p className="text-red-600 font-bold">Original Amount: {formatMoney(payload?.[0].value as any)}</p>
                  <p className="text-green-600 font-bold">Optimal Amount: {formatMoney(payload[1].value as any)}</p>
                </div>
              );
            }
            return null;
          }}
        />
        <Legend verticalAlign="top" wrapperStyle={{ lineHeight: "40px" }} />
        <ReferenceLine y={0} stroke="#000" />
        <Brush dataKey="name" height={30} stroke="#8884d8" />
        <Bar dataKey="original_amount" fill="#dc2626" />
        <Bar dataKey="optimal_amount" fill="#16a34a" />
      </RechartsBarChart>
    </ResponsiveContainer>
  );
}

export function ReAmountVerticalBarChart<
  T extends { id: string; amount: number },
  T_L extends keyof T,
  T_V extends Exclude<keyof T, T_L>,
>({ data, labelKey, valueKey }: { data: T[]; labelKey: T_L; valueKey: T_V }) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <RechartsBarChart
        width={500}
        height={300}
        data={data}
        margin={{
          top: 10,
          right: 30,
          left: 20,
          bottom: 5,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey={labelKey?.toString()} tickFormatter={(value) => value ?? ""} />
        <YAxis hide dataKey={valueKey?.toString()} tickFormatter={(value) => formatMoney(value)} />
        <Tooltip
          content={({ active, payload }) => {
            if (active && payload && payload.length) {
              return (
                <div className="p-2 bg-white shadow rounded border">
                  <p className="text-gray-700">{payload[0].payload.id}</p>
                  <p className="text-gray-800 font-bold">Amount: {formatMoney(payload?.[0].value as any)}</p>
                  {/* <p className="text-green-600 font-bold">Optimal Amount: {formatMoney(payload[1].value as any)}</p> */}
                </div>
              );
            }
            return null;
          }}
        />
        {/* <Legend verticalAlign="top" wrapperStyle={{ lineHeight: "40px" }} /> */}
        {/* <ReferenceLine y={0} stroke="#000" /> */}
        {/* <Brush dataKey="name" height={30} stroke="#8884d8" /> */}
        <Bar
          dataKey="amount"
          label={{
            position: "top",
            formatter: formatMoney,
            style: { fill: "#1f2937", fontWeight: "bold" },
            // content: ({ value }) => {
            //   return <text className="text-gray-800" />;
            // },
          }}
        >
          {data.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={
                {
                  0: "#22c55e",
                  1: "#ef4444",
                  2: "#d1d5db",
                }[index]
              }
            />
          ))}
        </Bar>
      </RechartsBarChart>
    </ResponsiveContainer>
  );
}

export function ReAmountHorizontalBarChart<
  T extends { id: string; amount: number },
  T_L extends keyof T,
  T_V extends Exclude<keyof T, T_L>,
>({ data, labelKey, valueKey }: { data: T[]; labelKey: T_L; valueKey: T_V }) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <RechartsBarChart
        layout="vertical"
        width={500}
        height={300}
        data={data}
        margin={{
          top: 5,
          left: 20,
          right: 20,
          bottom: 5,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis hide dataKey={valueKey?.toString()} type="number" tickFormatter={(value) => formatMoney(value) ?? ""} />
        <YAxis dataKey={labelKey?.toString()} type="category" />
        <Tooltip
          content={({ active, payload }) => {
            if (active && payload && payload.length) {
              return (
                <div className="p-2 bg-white shadow rounded border">
                  <p className="text-gray-700">{payload[0].payload[labelKey]}</p>
                  <p className="text-gray-800 font-bold">Amount: {formatMoney(payload?.[0].value as any)}</p>
                  {/* <p className="text-green-600 font-bold">Optimal Amount: {formatMoney(payload[1].value as any)}</p> */}
                </div>
              );
            }
            return null;
          }}
        />
        {/* <Legend verticalAlign="top" wrapperStyle={{ lineHeight: "40px" }} /> */}
        {/* <ReferenceLine y={0} stroke="#000" /> */}
        {/* <Brush dataKey="name" height={30} stroke="#8884d8" /> */}
        <Bar
          // layout={"vertical"}
          dataKey={valueKey?.toString()}
          label={
            //   {
            //   position: "center",
            //   formatter: formatMoney,
            //   style: { fill: "#1f2937", fontWeight: "bold" },
            //   // content: ({ value }) => {
            //   //   return <text className="text-gray-800" />;
            //   // },
            // }
            ({ value, index, x, y, width, height }) => {
              return (
                <text
                  x={x + width / 2}
                  y={y + height / 2 + 5} // 5 to manage font height
                  textAnchor="middle"
                  style={{
                    fontWeight: "bold",
                    fill: (
                      {
                        0: "#1f2937",
                        1: "white",
                        2: "#1f2937",
                      } satisfies Record<number, string>
                    )[index as number],
                  }}
                >
                  {formatMoney(value)}
                </text>
              );
            }
          }
        >
          {data.map((entry, index) => (
            <Fragment key={`cell-${index}`}>
              <Cell
                fill={
                  (
                    {
                      0: "#22c55e",
                      1: "#ef4444",
                      2: "#d1d5db",
                    } satisfies Record<number, string>
                  )[index as number]
                }
              />
            </Fragment>
          ))}
        </Bar>
      </RechartsBarChart>
    </ResponsiveContainer>
  );
}

export function ReCategorisedHorizontalBarChart<
  T extends Record<any, any>,
  T_L extends keyof T,
  T_V extends Exclude<keyof T, T_L>,
>({ data, labelKey, valueKey }: { data: T[]; labelKey: T_L; valueKey: T_V }) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <RechartsBarChart
        layout="vertical"
        width={500}
        height={300}
        data={data}
        margin={{
          top: 5,
          left: 100,
          right: 20,
          bottom: 5,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis hide dataKey={valueKey?.toString()} type="number" tickFormatter={(value) => formatMoney(value) ?? ""} />
        <YAxis
          dataKey={labelKey?.toString()}
          type="category"
          tick={({ x, y, payload }) => {
            // const text = toTitleCase(payload.value, { sep: "_" }) ?? "";
            // const paddingX = 4;
            // const paddingY = 2;
            // const fontSize = 12;

            // // Estimate width based on character count (monospace-style approximation)
            // const textWidth = text.length * (fontSize * 0.6);
            // const textHeight = fontSize + paddingY * 2;

            return (
              <text x={x} y={y} dy={4} textAnchor="end" style={{ whiteSpace: "nowrap" }}>
                <tspan className="text-sm fill-gray-600">{toTitleCase(payload.value, { sep: "_" })}</tspan>
                {/* <rect className="fill-gray-100 rounded-md size-3"></rect> */}
                {/* <tspan className="fill-gray-200">
                  <tspan>{data?.find((el) => el[labelKey] === payload?.value)?.[valueKey]}</tspan>
                </tspan> */}
              </text>

              // <g transform={`translate(${x}, ${y})`}>
              //   <rect x={x} y={y} width={paddingX * 2} height={paddingX * 2} fill="#f0f0f0" stroke="#ccc" rx={4} />
              //   <text
              //     x={-paddingX}
              //     y={4}
              //     textAnchor="end"
              //     fill="#333"
              //     fontSize={fontSize}
              //     style={{ whiteSpace: "nowrap" }}
              //   >
              //     {text}
              //   </text>
              // </g>
            );
          }}
        />
        <Tooltip
          content={({ active, payload }) => {
            if (active && payload && payload.length) {
              return (
                <div className="p-2 bg-white shadow rounded border">
                  <p className="text-gray-700">{toTitleCase(payload[0].payload[labelKey], { sep: "_" })}</p>
                  <p className="text-gray-800 font-bold"># of Transactions: {payload?.[0].value as any}</p>
                </div>
              );
            }
            return null;
          }}
        />
        <Bar
          // layout={"vertical"}
          dataKey={valueKey?.toString()}
          // label={({ value, index, x, y, width, height }) => {
          //   return (
          //     <text
          //       x={x + width / 2}
          //       y={y + height / 2 + 5} // 5 to manage font height
          //       textAnchor="middle"
          //       style={{
          //         fontWeight: "bold",
          //         fill: (
          //           {
          //             0: "#1f2937",
          //             1: "white",
          //             2: "#1f2937",
          //           } satisfies Record<number, string>
          //         )[index as number],
          //       }}
          //     >
          //       {formatMoney(value)}
          //     </text>
          //   );
          // }}
        >
          {data.map((entry, index) => (
            <Fragment key={`cell-${index}`}>
              <Cell
                fill={
                  // (
                  //   {
                  //     checks: "#f97316",
                  //     claims_and_insurance: "#d1d5db",
                  //     other_debit_card_purchases: "#a855f7",
                  //     payroll_and_benefits: "#14b8a6",
                  //     recurring_subscriptions: "#f43f5e",
                  //     tax_payments: "#0ea5e9",
                  //     uncategorized: "#ec4899",
                  //     vendor_and_service_payments: "#6366f1",
                  //     wire_transfers: "#22c55e",
                  //     withdrawals: "#ef4444",
                  //   } as Record<string, string>
                  // )
                  CHART_COLORS?.[index]
                }
              />
            </Fragment>
          ))}
        </Bar>
      </RechartsBarChart>
    </ResponsiveContainer>
  );
}

export function ReProfitVerticalBarChart<
  T extends Record<any, any>,
  T_L extends keyof T,
  T_V extends Exclude<keyof T, T_L>,
>({ data, labelKey, value1Key, value2Key }: { data: T[]; labelKey: T_L; value1Key: T_V; value2Key: T_V }) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <RechartsBarChart
        width={500}
        height={300}
        data={data}
        margin={{
          top: 10,
          right: 10,
          left: 10,
          bottom: 5,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <Legend formatter={(val) => toTitleCase(val, { sep: "_" })} />
        <XAxis
          dataKey={labelKey?.toString()}
          tickFormatter={(value) => dayjs(value, "YYYY-MM").format("MM/YY") ?? ""}
        />
        <YAxis dataKey={value1Key?.toString()} tickFormatter={(value) => formatCurrencyShort(value)} />
        <Tooltip
          content={({ active, payload }) => {
            if (active && payload && payload.length) {
              return (
                <div className="p-2 bg-white shadow rounded border">
                  <p className="text-gray-700">{dayjs(payload[0].payload.year_month, "YYYY-MM").format("MMMM YYYY")}</p>
                  <p className="text-gray-800 font-bold">
                    Profit with MCA: {formatMoney(payload?.[0].payload?.profit_with_mca as any)}
                  </p>
                  <p className="text-gray-800 font-bold">
                    Profit without MCA: {formatMoney(payload?.[0].payload?.profit_without_mca as any)}
                  </p>
                  {/* <p className="text-green-600 font-bold">Optimal Amount: {formatMoney(payload[1].value as any)}</p> */}
                </div>
              );
            }
            return null;
          }}
        />
        <Bar
          dataKey={value1Key?.toString()}
          fill="#6366f1"
          // label={{
          //   position: "top",
          //   formatter: formatMoney,
          //   style: { fill: "#1f2937", fontWeight: "bold" },
          //   // content: ({ value }) => {
          //   //   return <text className="text-gray-800" />;
          //   // },
          // }}
        />

        <Bar
          dataKey={value2Key?.toString()}
          fill="#14b8a6"
          // label={{
          //   position: "top",
          //   formatter: formatMoney,
          //   style: { fill: "#1f2937", fontWeight: "bold" },
          //   // content: ({ value }) => {
          //   //   return <text className="text-gray-800" />;
          //   // },
          // }}
        />
      </RechartsBarChart>
    </ResponsiveContainer>
  );
}

export function WeeklyDistributionPieChart<
  T extends { id: string; label: string; value: number; color: string },
  T_K extends keyof T,
>({ data, dataKey }: { data: T[]; dataKey: T_K }) {
  // console.log({ "weekly data": data });
  return (
    <ResponsivePie /* or Pie for fixed dimensions */
      data={data}
      margin={{ top: 40, right: 80, bottom: 80, left: 80 }}
      colors={{ datum: "data.color" }}
      innerRadius={0.5}
      padAngle={0.6}
      cornerRadius={2}
      activeOuterRadiusOffset={8}
      arcLinkLabelsSkipAngle={10}
      arcLinkLabelsTextColor="#333333"
      arcLinkLabelsThickness={2}
      arcLinkLabelsColor={{ from: "color" }}
      arcLabelsSkipAngle={10}
      arcLabelsTextColor={{ from: "color", modifiers: [["brighter", 10]] }}
      // legends={[
      //   {
      //     anchor: "bottom",
      //     direction: "row",
      //     translateY: 56,
      //     itemWidth: 100,
      //     itemHeight: 18,
      //     symbolShape: "circle",
      //   },
      // ]}
      valueFormat={(value) => Number(value).toFixed(0) + "%"}
      arcLinkLabel={(d) => `${d.label}: ${Number(d.value).toFixed(0) + "%"}`}
      tooltip={(d) => {
        return (
          <div className="bg-white px-2 py-1 rounded-md shadow-sm shadow-gray-300/50 inline-flex gap-1 items-center">
            <div className="size-3 rounded-sm" style={{ background: d.datum.color }} />
            <span>{d.datum.label}</span>
            <span className="font-bold">{Number(d.datum.value).toFixed(0) + "%"}</span>
          </div>
        );
      }}
    />
  );
}

export function ReMCAProfitPercentageVerticalBarChart<
  T extends Record<any, any>,
  T_L extends keyof T,
  T_V extends Exclude<keyof T, T_L>,
>({ data, labelKey, value1Key, value2Key }: { data: T[]; labelKey: T_L; value1Key: T_V; value2Key: T_V }) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <RechartsBarChart
        width={500}
        height={300}
        data={data}
        margin={{
          top: 10,
          right: 10,
          left: 10,
          bottom: 5,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <Legend formatter={(val) => toTitleCase(val, { sep: "_" })} />
        <XAxis
          dataKey={labelKey?.toString()}
          tickFormatter={(value) => dayjs(value, "YYYY-MM").format("MM/YY") ?? ""}
        />
        <YAxis dataKey={value1Key?.toString()} tickFormatter={(value) => formatCurrencyShort(value)} />
        <Tooltip
          content={({ active, payload }) => {
            if (active && payload && payload.length) {
              return (
                <div className="p-2 bg-white shadow rounded border">
                  <p className="text-gray-700">{dayjs(payload[0].payload.year_month, "YYYY-MM").format("MMMM YYYY")}</p>
                  <p className="text-gray-800 font-bold">
                    Profit without MCA: {formatMoney(payload?.[0].payload?.profit_without_mca as any)}
                  </p>
                  <p className="text-gray-800 font-bold">MCA: {formatMoney(payload?.[0].payload?.mca as any)}</p>
                  <p className="text-green-600 font-bold">
                    MCA Percentage:{" "}
                    {formatPercent((payload[0]?.payload.mca / payload?.[0].payload?.profit_without_mca) as any)}
                  </p>
                </div>
              );
            }
            return null;
          }}
        />
        <Bar
          dataKey={value1Key?.toString()}
          fill="#14b8a6"
          stackId={"a"}
          // label={{
          //   position: "top",
          //   formatter: formatMoney,
          //   style: { fill: "#1f2937", fontWeight: "bold" },
          //   // content: ({ value }) => {
          //   //   return <text className="text-gray-800" />;
          //   // },
          // }}
        />

        <Bar
          dataKey={value2Key?.toString()}
          stackId={"a"}
          fill="#f43f5e"
          // label={{
          //   position: "top",
          //   formatter: formatMoney,
          //   style: { fill: "#1f2937", fontWeight: "bold" },
          //   // content: ({ value }) => {
          //   //   return <text className="text-gray-800" />;
          //   // },
          // }}
        />
      </RechartsBarChart>
    </ResponsiveContainer>
  );
}

export function ReCategoryMonthWise<
  T extends Record<string, any>,
  T_L extends keyof T,
  T_V extends Exclude<keyof T, T_L>,
>({ data, labelKey, valueKey }: { data: T[]; labelKey: T_L; valueKey: T_V }) {
  const CustomNameLabel: React.FC<any> = ({ x, y, index, payload }: any) => {
    const name = data[index][labelKey];

    return (
      <>
        <Text x={x} y={y! - 15} className="fill-gray-600 text-sm" textAnchor="start">
          {name}
        </Text>
        {/* <Text x={x} y={y! + 15} className="fill-gray-600 text-sm" textAnchor="end">
          {data[index][valueKey]}
        </Text> */}
      </>
    );
  };
  return (
    <ResponsiveContainer width="100%" height="100%">
      <RechartsBarChart
        layout="vertical"
        width={700}
        height={300}
        data={data}
        margin={{ top: 20, right: 100, left: 0, bottom: 20 }}
        barCategoryGap={20}
      >
        {/* <CartesianGrid strokeDasharray="3 3" /> */}
        <XAxis type="number" hide tickFormatter={(val) => `${formatMoney(val)}`} />
        <YAxis type="category" hide dataKey={labelKey?.toString()} width={100} tick={{ fontSize: 14 }} />
        <Tooltip formatter={(val) => `${val}%`} />
        <Bar dataKey={valueKey?.toString()}>
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
          ))}
          {/* <LabelList
            dataKey={valueKey?.toString()}
            position="right"
            formatter={(val: number) => `${formatMoney(val)}`}
          /> */}
          {/* Label name above bar */}
          <LabelList content={<CustomNameLabel />} />
          {/* Percentage to the right */}
          <LabelList
            dataKey={valueKey?.toString()}
            position="right"
            className="fill-gray-800 font-semibold"
            formatter={formatMoney}
          />
        </Bar>
      </RechartsBarChart>
    </ResponsiveContainer>
  );
}

export function WithdrawalPercentageBarChart({
  withdrawal_percentage,
}: {
  withdrawal_percentage: Record<string, { mca_total: number; percent: number; revenue: number }>;
}) {
  const data = Object.entries(withdrawal_percentage)
    .map(([key, value]) => ({
      name: dayjs(key).format("MM/YY"),
      net_revenue: value.revenue - value.mca_total,
      withdrawal_amount: value.mca_total,
      withdrawal_percentage: value.percent,
      revenue: value.revenue,
    }))
    .sort((a, b) => {
      return Number(a.name?.replace("/", "")) - Number(b.name?.replace("/", ""));
    });

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border px-3 py-2 rounded shadow">
          <p className="text-sm font-semibold">{dayjs(label, "MM/YY").format("MMMM YYYY")}</p>
          <p className="text-xs">Gross Revenue: {formatMoney(payload[0].payload.revenue)}</p>
          <p className="text-xs">Withdrawal Amount: {formatMoney(payload[0].payload.withdrawal_amount)}</p>
          <p className="text-xs">Net Revenue: {formatMoney(payload[0].payload.net_revenue)}</p>
          <p className="text-xs">
            Withdrawal Percentage:{" "}
            <span className="text-green-600 font-semibold">
              {formatPercent(payload[0].payload.withdrawal_percentage)}
            </span>
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <RechartsBarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" />
        <YAxis tickFormatter={(val) => formatCurrencyShort(val)} />
        <Tooltip content={<CustomTooltip />} />
        <Legend />
        <Bar dataKey="net_revenue" name="Net Revenue" stackId="a" fill="#6366f1" />
        <Bar dataKey="withdrawal_amount" name="Withhold Amount" stackId="a" fill="#14b8a6">
          <LabelList
            dataKey="withdrawal_percentage"
            position="top"
            formatter={(value: number) => formatPercent(value)}
          />
        </Bar>
      </RechartsBarChart>
    </ResponsiveContainer>
  );
}

export function CategoryCreditTransactionPieChart({
  total_categorised_credit_transactions,
}: {
  total_categorised_credit_transactions: Record<string, { amount: number; count: number; percentage: number }>;
}) {
  const data = Object.entries(total_categorised_credit_transactions).map(([key, value]) => ({
    name: key,
    total_transactions: value.count,
    amount: value.amount,
  }));

  return (
    <ResponsiveContainer width="100%" height="100%">
      <RechartsPieChart width={400} height={400}>
        <Pie data={data} dataKey="total_transactions" cx="50%" cy="50%" outerRadius={60}>
          {data.map((_, index) => (
            <Cell key={`count-cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
          ))}
        </Pie>

        <Pie
          data={data}
          dataKey="amount"
          cx="50%"
          cy="50%"
          innerRadius={70}
          outerRadius={90}
          label={({ name, total_transactions, amount }) => `${name}: ${formatMoney(amount)} (${total_transactions})`}
        >
          {data.map((_, index) => (
            <Cell key={`amount-cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
          ))}
        </Pie>
      </RechartsPieChart>
    </ResponsiveContainer>
  );
}

export function ReNegativeDays<T extends Record<string, any>, T_L extends keyof T, T_V extends Exclude<keyof T, T_L>>({
  data,
  labelKey,
  valueKey,
}: {
  data: T[];
  labelKey: T_L;
  valueKey: T_V;
}) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <RechartsBarChart
        width={500}
        height={300}
        data={data}
        margin={{ top: 20, right: 10, left: 0, bottom: 20 }}
        // barCategoryGap={20}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey={labelKey?.toString()}
          tickFormatter={(value) => dayjs(value, "YYYY-MM").format("MM/YY") ?? ""}
        />
        <YAxis dataKey={valueKey?.toString()} tick={{ fontSize: 14 }} />
        <Tooltip />
        <Bar dataKey={valueKey?.toString()} fill={CHART_COLORS[6]} />
      </RechartsBarChart>
    </ResponsiveContainer>
  );
}

export function ReNsfTransactions<
  T extends Record<string, any>,
  T_L extends keyof T,
  T_V extends Exclude<keyof T, T_L>,
>({ data, labelKey, valueKey }: { data: T[]; labelKey: T_L; valueKey: T_V }) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <RechartsBarChart
        width={500}
        height={300}
        data={data}
        margin={{ top: 20, right: 10, left: 0, bottom: 20 }}
        // barCategoryGap={20}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey={labelKey?.toString()}
          tickFormatter={(value) => dayjs(value, "YYYY-MM").format("MM/YY") ?? ""}
        />
        <YAxis dataKey={valueKey?.toString()} tick={{ fontSize: 14 }} />
        <Tooltip />
        <Bar dataKey={valueKey?.toString()} fill={CHART_COLORS[2]} />
      </RechartsBarChart>
    </ResponsiveContainer>
  );
}
